import { useEffect, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { CalendarClock, CalendarDays, GraduationCap, Users, MapPin, Clock, ArrowRight, Printer, FileDown, Loader2 } from "lucide-react";
import { proApi, downloadProPdf } from "@/lib/proApi";

const Card = ({ icon: Icon, value, label, testid }) => (
  <div className="bg-[#0F0F0F] border border-white/10 p-5 md:p-6" data-testid={testid}>
    <Icon className="text-[#ff0033]" size={22} />
    <p className="font-heading text-4xl md:text-5xl text-white mt-3 leading-none">{value}</p>
    <p className="font-mono-lab text-[10px] uppercase tracking-widest text-neutral-500 mt-2">{label}</p>
  </div>
);

const ProDashboard = () => {
  const { user } = useOutletContext();
  const nav = useNavigate();
  const [d, setD] = useState(null);

  const load = () => proApi.get("/pro/dashboard").then((r) => setD(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  if (!d) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#ff0033]" /></div>;

  return (
    <div data-testid="pro-dashboard">
      <h1 className="font-heading text-3xl md:text-4xl uppercase tracking-tight">Willkommen im <span className="text-[#ff0033]">Pro Portal</span></h1>
      <p className="text-neutral-500 text-xs mt-1">Zuletzt aktualisiert: {new Date(d.updated_at).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Berlin" })} Uhr</p>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
        <Card icon={CalendarClock} value={d.today} label="Heute stattfindend" testid="pro-stat-today" />
        <Card icon={CalendarDays} value={d.next7} label="Nächste 7 Tage" testid="pro-stat-week" />
        <Card icon={GraduationCap} value={d.mine} label="Kommende Kurse" testid="pro-stat-mine" />
        <Card icon={Users} value={d.total_participants} label="Angemeldete Teilnehmer" testid="pro-stat-participants" />
      </div>

      <h2 className="font-heading text-xl uppercase tracking-tight mt-10 mb-3 flex items-center gap-2"><span className="w-2 h-2 bg-[#ff0033] rounded-full animate-pulse" /> Die nächsten Kurse</h2>
      {(d.next_courses && d.next_courses.length > 0) ? (
        <div className="space-y-4" data-testid="pro-next-courses">
          {d.next_courses.map((nc) => (
            <div key={nc.session_id} className="bg-[#0F0F0F] border border-[#ff0033]/40 p-6" data-testid={`pro-next-course-${nc.session_id}`}>
              <h3 className="font-heading text-2xl md:text-3xl uppercase tracking-tight text-white">{nc.name}</h3>
              {nc.title && <p className="text-[#ff0033] text-sm mt-1">{nc.title}</p>}
              <div className="mt-4 border border-white/10 bg-black/40 px-4 py-3 inline-flex flex-wrap items-baseline gap-x-5 gap-y-1" data-testid={`pro-next-datetime-${nc.session_id}`}>
                <span className="font-heading text-2xl md:text-3xl uppercase tracking-tight text-white flex items-center gap-2.5">
                  <CalendarDays size={22} className="text-[#ff0033] shrink-0" />
                  {new Date(nc.start).toLocaleDateString("de-DE", { weekday: "long", day: "2-digit", month: "2-digit", year: "numeric", timeZone: "Europe/Berlin" })}
                </span>
                <span className="font-heading text-2xl md:text-3xl uppercase tracking-tight text-[#ff0033] flex items-center gap-2.5">
                  <Clock size={22} className="shrink-0" />
                  {new Date(nc.start).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Berlin" })}
                  {nc.end ? ` – ${new Date(nc.end).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Berlin" })}` : ""} Uhr
                </span>
              </div>
              <div className="grid sm:grid-cols-2 gap-x-8 gap-y-2 mt-4 text-sm text-neutral-300">
                <span className="flex items-center gap-2"><MapPin size={15} className="text-[#ff0033]" /> {nc.location || "—"}</span>
                <span className="flex items-center gap-2"><Users size={15} className="text-[#ff0033]" /> {nc.participants} Teilnehmer{nc.capacity ? ` / ${nc.capacity}` : ""}</span>
              </div>
              <div className="flex flex-wrap gap-3 mt-6">
                <button onClick={() => nav(`/pro/courses/${nc.course_id}`)} data-testid={`pro-next-open-${nc.session_id}`} className="bg-[#ff0033] text-white font-heading uppercase tracking-wider px-5 py-3 text-sm hover:bg-[#cc0029] transition-colors flex items-center gap-2">Kurs öffnen <ArrowRight size={15} /></button>
                <button onClick={() => nav(`/pro/print/${nc.session_id}`)} className="border border-white/20 text-white font-heading uppercase tracking-wider px-5 py-3 text-sm hover:border-[#ff0033] transition-colors flex items-center gap-2"><Printer size={15} /> Teilnehmerliste drucken</button>
                <button onClick={() => downloadProPdf(`/pro/sessions/${nc.session_id}/participants.pdf`, "Teilnehmerliste.pdf")} className="border border-white/20 text-white font-heading uppercase tracking-wider px-5 py-3 text-sm hover:border-[#ff0033] transition-colors flex items-center gap-2"><FileDown size={15} /> PDF herunterladen</button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-neutral-500">Keine kommenden Kurse.</p>
      )}
    </div>
  );
};

export default ProDashboard;
